import { createAsyncThunk } from '@reduxjs/toolkit';
import { addLoading, removeLoading, showError } from '../appAction';
import { Enums, Paths, Value } from '@utils/constants';
import paths from '@utils/constants/paths';
import { axios } from '@utils/lib';
import { IDriverApproveRes, IListRes } from '@interfaces/apis/response-data';
import { store } from '@redux/store';
import { t } from 'i18next';

export const getDriverListAsync = createAsyncThunk(
  'api/getDriverListAsync',
  async (query: IDriverManagementQuery, { rejectWithValue, dispatch }) => {
    dispatch(addLoading());
    const searchQuery: Record<string, string> = {
      page: String(query.page ?? Value.TABLE_PAGE_INIT),
    };
    if (query.accessTime) searchQuery.accessTime = query.accessTime;
    if (query.exitTime) searchQuery.exitTime = query.exitTime;
    if (query.companyName) searchQuery.companyName = query.companyName;
    if (query.deviceId) searchQuery.deviceId = query.deviceId;
    if (query.fullNameUser) searchQuery.fullNameUser = query.fullNameUser;
    if (query.phoneNumber) searchQuery.phoneNumber = query.phoneNumber;
    if (query.stayingStart) searchQuery.stayingStart = query.stayingStart;
    if (query.stayingEnd) searchQuery.stayingEnd = query.stayingEnd;
    if (query.registerDateStart)
      searchQuery.registerDateStart = query.registerDateStart;
    if (query.registerDateEnd)
      searchQuery.registerDateEnd = query.registerDateEnd;
    if (query.status) searchQuery.status = query.status;

    searchQuery.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const params = new URLSearchParams(searchQuery).toString();
    try {
      const { data } = await axios.request<IListRes<IDriverManagement>>(
        Enums.HTTP_METHOD.GET,
        paths.DRIVER_MANAGEMENT + '?' + params
      );
      return data;
    } catch (error) {
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  }
);

export const getDriverDetailAsync = createAsyncThunk(
  'api/getDriverDetailsAsync',
  async (query: IDriverDetail, { rejectWithValue, dispatch }) => {
    const { id } = query;
    dispatch(addLoading());
    try {
      const result = await axios.request<IPreRegistrationDetailRes>(
        Enums.HTTP_METHOD.GET,
        Paths.DRIVER_MANAGEMENT_DETAIL.replace(':id', id)
      );
      return result.data;
    } catch (error) {
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  }
);

export const deleteDriverAsync = createAsyncThunk(
  'api/deleteDriveAsync',
  async (body: IUserDeleteForm, { rejectWithValue, dispatch }) => {
    dispatch(addLoading());
    try {
      const { data } = await axios.request(
        Enums.HTTP_METHOD.POST,
        Paths.DELETE_DRIVER,
        body
      );
      return data;
    } catch (error) {
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  }
);

export const registerOPTAsync = createAsyncThunk(
  'api/registerOPTAsync',
  async (_, { rejectWithValue, dispatch }) => {
    dispatch(addLoading());

    try {
      const { data } = await axios.request<IOtpDriver>(
        Enums.HTTP_METHOD.POST,
        paths.REGISTER_OTP_DRIVER
      );
      return data;
    } catch (error) {
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  }
);

export const verifyOtpAsync = createAsyncThunk(
  'api/verifyOtpAsync',
  async (
    payload: { otp: string; isShowError?: boolean },
    { rejectWithValue, dispatch }
  ) => {
    dispatch(addLoading());
    const { otp, isShowError = true } = payload;
    const searchQuery: Record<string, string> = { otp: String(otp) };
    const params = new URLSearchParams(searchQuery).toString();
    try {
      const { data } = await axios.request<IListRes<IOtpDriver>>(
        Enums.HTTP_METHOD.GET,
        paths.VERIFY_OTP_DRIVER + '?' + params
      );
      return data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (isShowError) {
        store.dispatch(showError({ message: t(error?.response.data.message) }));
      }
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  }
);

export const driverRegisterAsync = createAsyncThunk(
  'api/driverRegisterAsync',
  async (query: IDriverRegister, { rejectWithValue, dispatch }) => {
    dispatch(addLoading());
    const formData = new FormData();
    formData.append('companyName', query.companyName);
    formData.append('fullNameUser', query.fullNameUser);
    formData.append('phoneNumber', query.phoneNumber);
    formData.append('deliveryDestination', query.deliveryDestination);
    formData.append('carNumber', query.carNumber);
    formData.append('tenantId', String(query.tenantId));
    formData.append('isUsedOneTime', String(query.isUsedOneTime));
    formData.append('faceImage', query.file);

    try {
      const { data } = await axios.request<IDriverManagement>(
        Enums.HTTP_METHOD.POST,
        Paths.DRIVER_MANAGEMENT,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return data;
    } catch (error: unknown) {
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  }
);

export const driverRegisterOTPAsync = createAsyncThunk(
  'api/driverRegisterOTPAsync',
  async (query: IDriverRegister, { rejectWithValue, dispatch }) => {
    dispatch(addLoading());
    const formData = new FormData();
    formData.append('companyName', query.companyName);
    formData.append('fullNameUser', query.fullNameUser);
    formData.append('phoneNumber', query.phoneNumber);
    formData.append('deliveryDestination', query.deliveryDestination);
    formData.append('carNumber', query.carNumber);
    formData.append('isUsedOneTime', String(query.isUsedOneTime));
    formData.append('faceImage', query.file);
    formData.append('otp', query.otp);
    try {
      const { data } = await axios.request<IDriverManagement>(
        Enums.HTTP_METHOD.POST,
        Paths.OTP_DRIVER_REGISTER,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return data;
    } catch (error: unknown) {
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  }
);

export const checkImageQualityAsync = createAsyncThunk(
  'api/checkImageQualityAsync',
  async (file: File, { rejectWithValue, dispatch }) => {
    dispatch(addLoading());

    const formData = new FormData();
    formData.append('faceImage', file);

    try {
      const { data } = await axios.request<{ isPass: boolean }>(
        Enums.HTTP_METHOD.POST,
        Paths.CHECK_IMG_QUALITY,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return data;
    } catch (error) {
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  }
);

export const driverApproveAsync = createAsyncThunk(
  'api/driver-approve',
  async (id: string, { rejectWithValue, dispatch }) => {
    dispatch(addLoading());
    try {
      const { data } = await axios.request<IDriverApproveForm>(
        Enums.HTTP_METHOD.PATCH,
        Paths.DRIVER_MANAGEMENT_APPROVE.replace(':id', id)
      );
      return data;
    } catch (error: unknown) {
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  }
);

export const driverApproveBulkAsync = createAsyncThunk(
  'api/driver-approve-bulk',
  async (body: IApproveAllDriverBase, { rejectWithValue, dispatch }) => {
    dispatch(addLoading());
    try {
      const { data } = await axios.request<IDriverApproveRes>(
        Enums.HTTP_METHOD.PATCH,
        Paths.DRIVER_MANAGEMENT_APPROVE_BULK,
        body
      );
      return data;
    } catch (error: unknown) {
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  }
);

export const updateDriverDetailsAsync = createAsyncThunk(
  'api/updateDriverDetailsAsync',
  async (query: IUpdateDriverDetail, { rejectWithValue, dispatch }) => {
    const {
      id,
      isAlert,
      note,
      companyName,
      fullNameUser,
      phoneNumber,
      deliveryDestination,
      carNumber,
    } = query;
    const body = {
      note,
      isAlert,
      companyName,
      fullNameUser,
      phoneNumber,
      deliveryDestination,
      carNumber,
    };
    dispatch(addLoading());
    try {
      const result = await axios.request<IPreRegistrationDetailRes>(
        Enums.HTTP_METHOD.PATCH,
        Paths.DRIVER_MANAGEMENT_DETAIL.replace(':id', id),
        body
      );
      return result.data;
    } catch (error) {
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  }
);

export const getWokingSessionAsync = createAsyncThunk(
  'api/getWokingSessionAsync',
  async (query: IWorkingSession, { rejectWithValue, dispatch }) => {
    dispatch(addLoading());
    const searchQuery: Record<string, string> = {
      driverId: String(query.id),
    };
    const params = new URLSearchParams(searchQuery).toString();
    try {
      const result = await axios.request<IListRes<IDriverManagementList>>(
        Enums.HTTP_METHOD.GET,
        Paths.RECEPTION_MANAGEMENT + '?' + params
      );
      return result.data;
    } catch (error) {
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  }
);

export const approveDriverIdAsync = createAsyncThunk(
  'api/approveDriverIdAsync',
  async (query: IApproveDriverId, { rejectWithValue, dispatch }) => {
    const { id, status } = query;
    const body = {
      status,
    };
    dispatch(addLoading());
    try {
      const result = await axios.request<IApprovePreRegistrationRes>(
        Enums.HTTP_METHOD.PATCH,
        Paths.DRIVER_APPROVE_ID.replace(':id', id),
        body
      );
      return result.data;
    } catch (error) {
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  }
);

export const deleteDriverIdAsync = createAsyncThunk(
  'api/deleteDriverIdAsync',
  async (id: string, { rejectWithValue, dispatch }) => {
    dispatch(addLoading());
    try {
      const { data } = await axios.request(
        Enums.HTTP_METHOD.DELETE,
        Paths.DRIVER_MANAGEMENT_DETAIL.replace(':id', id)
      );
      return data;
    } catch (error) {
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  }
);

export const verifyPhoneDriverAsync = createAsyncThunk(
  'api/verifyPhoneDriverAsync',
  async (hashId: string, { rejectWithValue, dispatch }) => {
    dispatch(addLoading());
    try {
      const { data } = await axios.request(
        Enums.HTTP_METHOD.PATCH,
        Paths.VERIFY_PHONE_DRIVER.replace(':hashId', hashId)
      );
      return data;
    } catch (error) {
      return rejectWithValue(error);
    } finally {
      dispatch(removeLoading());
    }
  }
);
